import { FC, ReactNode, useRef } from 'react';
import { Variants } from 'framer-motion';
import { useDetectOutsideClick } from 'hooks/useDetectOutsideClick';
import { STooltip, STooltipContainer, STooltipParagraph } from './styled';

type TooltipPosition = 'top' | 'right' | 'bottom' | 'left' | 'absolute';

type Props = {
  children: ReactNode;
  paragraph: string;
  position?: TooltipPosition;
};

const variants: Variants = {
  visible: {
    display: 'flex',
    opacity: 1,
    transition: {
      display: {
        duration: 0.05,
      },
      opacity: {
        duration: 0.05,
      },
    },
  },
  hidden: {
    display: 'none',
    opacity: 0,
    transition: {
      display: {
        delay: 0.5,
        duration: 0.05,
      },
      opacity: {
        duration: 0.05,
      },
    },
  },
};

const Tooltip: FC<Props> = ({ children, position = 'absolute', paragraph }) => {
  const containerRef = useRef(null);
  const hoverRef = useRef(false);
  const [isVisible, setIsVisible] = useDetectOutsideClick(
    [containerRef],
    false
  );

  const onClick = () => {
    if (!hoverRef.current) {
      setIsVisible(!isVisible);
    }
  };

  const onMouseEnter = () => {
    hoverRef.current = true;
    setIsVisible(true);
  };

  const onMouseLeave = () => {
    hoverRef.current = false;
    setIsVisible(false);
  };

  return (
    <STooltipContainer
      ref={containerRef}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-testid="tooltip-container"
    >
      {children}
      <STooltip
        variants={variants}
        animate={isVisible ? 'visible' : 'hidden'}
        transition={{ duration: 0.1, type: 'tween' }}
        initial={false}
        position={position}
      >
        <STooltipParagraph>{paragraph}</STooltipParagraph>
      </STooltip>
    </STooltipContainer>
  );
};

export default Tooltip;
